import { useRef, useState } from "react";
import { Button } from "@/components/ui";
import "./BuildCostTab.css";
import {
  buildCostExcel,
  buildCostPdf,
  calculateEstimate,
  costElementSortKey,
  costInputsFromQuantities,
  detectAbnormals,
  generateNarrative,
  generateSow,
  inferSpec,
  parseIfc,
  SPEC_AXES,
  type Abnormal,
  type CostPlan,
  type ExportMethod,
  type IfcQuantities,
  type Sow,
  type SpecInference,
} from "@/api/buildCost";

const GBP = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 });
const PROPERTY_TYPES = ["detached", "semi_detached", "terrace", "end_terrace", "flat"];

interface Props {
  jobId: number;
  jobNumber: number;
  defaultAddress?: string;
  defaultPostcode?: string;
  /** Job UPRN — passed to abnormals detection so the engine treats the address as
   *  register-matched (exact), refining the site risk commentary on the report. */
  defaultUprn?: string;
  clientName?: string;
  onError: (msg: string | undefined) => void;
  onNotice?: (kind: "info" | "warn", message: string) => void;
}

type Phase = "upload" | "parsed" | "done";

export function BuildCostTab({ jobId, jobNumber, defaultAddress = "", defaultPostcode = "", defaultUprn = "", clientName = "", onError, onNotice }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [exportMethod, setExportMethod] = useState<ExportMethod>("layer_diff");
  const [propertyType, setPropertyType] = useState("semi_detached");

  const [phase, setPhase] = useState<Phase>("upload");
  const [busy, setBusy] = useState<"" | "parsing" | "generating" | "pdf" | "xlsx">("");
  const [quantities, setQuantities] = useState<IfcQuantities | null>(null);
  const [spec, setSpec] = useState<Record<string, string>>({});
  const [specInfo, setSpecInfo] = useState<SpecInference | null>(null);
  // Staff-confirmed kitchen/bathroom flags. The parser can't tell whether a detected
  // kitchen/bathroom sits in the new-build zone, so these stay blank until staff opt in.
  const [includeKitchen, setIncludeKitchen] = useState(false);
  const [includeBathroom, setIncludeBathroom] = useState(false);
  const [costPlan, setCostPlan] = useState<CostPlan | null>(null);
  const [abnormals, setAbnormals] = useState<Abnormal[]>([]);
  const [sow, setSow] = useState<Sow | null>(null);
  const [narrative, setNarrative] = useState<string>("");

  const fail = (err: unknown) => onError(err instanceof Error ? err.message : "Build-cost request failed.");

  async function parse() {
    if (!file || busy) return;
    setBusy("parsing");
    onError(undefined);
    try {
      const q = await parseIfc(file, exportMethod, jobId);
      setQuantities(q);
      // Kitchen/bathroom checkboxes stay blank — staff opt in; we don't seed them from the parser.
      // Pre-fill the spec selections from inference (editable below). roof_type is overridden with
      // the parser's geometry-based detection — authoritative over the text-based AI guess, which
      // mis-reads existing tile-roof class names and tags a flat extension as pitched.
      const roofOverride: Record<string, string> = q.detected_roof_type ? { roof_type: q.detected_roof_type } : {};
      try {
        const inf = await inferSpec(q.ifc_classes);
        setSpec({ ...inf.spec_selections, ...roofOverride });
        setSpecInfo(inf);
      } catch {
        setSpec({ ...Object.fromEntries(SPEC_AXES.map((a) => [a.key, a.values[0]])), ...roofOverride });
      }
      setPhase("parsed");
      onNotice?.("info", `Parsed ${q.source_file} — confidence: ${q.extraction_confidence}`);
    } catch (err) {
      fail(err);
    } finally {
      setBusy("");
    }
  }

  async function generate() {
    if (!quantities || busy) return;
    setBusy("generating");
    onError(undefined);
    try {
      const sowResult = await generateSow({
        quantities,
        spec_selections: spec,
        address: defaultAddress,
        application_type: "householder",
        job_id: jobId,
        include_kitchen: includeKitchen,
        include_bathroom: includeBathroom,
      });
      setSow(sowResult);

      const inputs = costInputsFromQuantities(quantities, { includeKitchen, includeBathroom });
      const rawPlan = await calculateEstimate(inputs, spec, jobId);
      // Apply IFC-derived door label overrides (e.g. French doors with sidelights) to the cost table —
      // the rate/quantity is unchanged; only the displayed label is more specific. Flows to the
      // breakdown, PDF and Excel (all read this cost plan).
      const labelOverrides = quantities.door_label_overrides ?? {};
      const plan: CostPlan = Object.keys(labelOverrides).length
        ? { ...rawPlan, elements: rawPlan.elements.map((e) => (labelOverrides[e.element] ? { ...e, label: labelOverrides[e.element] } : e)) }
        : rawPlan;
      setCostPlan(plan);

      const abn = await detectAbnormals({
        postcode: defaultPostcode,
        // The full site address grounds the engine's town/area reasoning.
        address: defaultAddress || undefined,
        // Omit when blank so the engine sees "no UPRN" (less-certain location) rather than an
        // empty string — present ⇒ the address is register-matched, sharpening the risk commentary.
        uprn: defaultUprn || undefined,
        property_type: propertyType,
        application_type: "householder",
      });
      // Oversized windows (> 2.50 m²) are excluded from the per-unit rates by the parser — surface
      // them as an advisory abnormal alongside the site risks.
      const abnormals: Abnormal[] = [...abn.abnormals];
      const oversized = quantities.oversized_windows ?? [];
      if (oversized.length > 0) {
        const largest = Math.max(...oversized.map((w) => w.area));
        abnormals.push({
          risk: `${oversized.length} oversized window${oversized.length > 1 ? "s" : ""} (over 2.50 m²)`,
          likelihood: "n/a",
          reason: `Excluded from the standard per-unit window rates (largest ${largest.toFixed(2)} m²). Bespoke or large glazed units to be priced separately.`,
          cost_indicative: "Price separately",
        });
      }
      // Doors whose type couldn't be classified are excluded from the banded door rates — flag them.
      const unclassifiedDoors = quantities.unclassified_doors ?? [];
      if (unclassifiedDoors.length > 0) {
        abnormals.push({
          risk: `${unclassifiedDoors.length} unclassified door${unclassifiedDoors.length > 1 ? "s" : ""}`,
          likelihood: "n/a",
          reason: "Door type could not be read from the model — excluded from the banded door rates. Review the model and price these doors separately.",
          cost_indicative: "Price separately",
        });
      }
      setAbnormals(abnormals);

      const narr = await generateNarrative({
        cost_plan: plan,
        spec_selections: spec,
        abnormals,
        address: defaultAddress,
        application_type: "householder",
      });
      setNarrative(narr.narrative);

      setPhase("done");
      onNotice?.("info", `Estimate ready — ${GBP.format(plan.total)}`);
    } catch (err) {
      fail(err);
    } finally {
      setBusy("");
    }
  }

  async function downloadPdf() {
    if (!costPlan || busy) return;
    setBusy("pdf");
    onError(undefined);
    try {
      const { pdf_url } = await buildCostPdf({
        sow: sow ?? undefined,
        cost_plan: costPlan,
        abnormals,
        narrative,
        place: defaultAddress,
        client_name: clientName || undefined,
        pdf_name: `build-cost-${jobNumber}.pdf`,
      });
      const blob = await (await fetch(pdf_url)).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `build-cost-${jobNumber}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      fail(err);
    } finally {
      setBusy("");
    }
  }

  async function downloadExcel() {
    if (!file || !costPlan || busy) return;
    setBusy("xlsx");
    onError(undefined);
    try {
      const { xlsx_url } = await buildCostExcel({
        file,
        exportMethod,
        sow: sow ?? undefined,
        costPlan,
        address: defaultAddress,
        jobNumber,
      });
      const blob = await (await fetch(xlsx_url)).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `build-cost-${jobNumber}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      fail(err);
    } finally {
      setBusy("");
    }
  }

  const nb = quantities?.quantities?.new_build;

  return (
    <div className="buildcost-tab" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* 1 — Upload */}
      <section>
        <h3 style={{ margin: "0 0 8px" }}>1 · Upload IFC model</h3>
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <input
            ref={fileRef}
            type="file"
            accept=".ifc"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <fieldset style={{ border: "none", padding: 0, margin: 0, display: "flex", gap: 14 }}>
            <label><input type="radio" name="export_method" checked={exportMethod === "layer_diff"} onChange={() => setExportMethod("layer_diff")} /> Layer diff</label>
            <label><input type="radio" name="export_method" checked={exportMethod === "phased"} onChange={() => setExportMethod("phased")} /> Phased</label>
          </fieldset>
          <Button loading={busy === "parsing"} disabled={!file} onClick={parse}>Parse IFC</Button>
        </div>
      </section>

      {/* 2 — Parsed quantities + warnings + spec review */}
      {phase !== "upload" && quantities && (
        <section>
          <h3 style={{ margin: "0 0 8px" }}>2 · Quantities & specification</h3>
          <p style={{ margin: "0 0 8px", color: "var(--fg-muted)" }}>
            {quantities.source_file} · confidence <strong>{quantities.extraction_confidence}</strong>
            {nb && (
              <>
                {" "}· new build: {nb.external_wall_area ?? 0} m² ext wall, {nb.roof_plan_area ?? 0} m² roof,
                {" "}{nb.window_count ?? 0} windows{nb.roof_light_count ? `, ${nb.roof_light_count} roof light(s)` : ""}
              </>
            )}
          </p>
          {quantities.warnings.length > 0 && (
            <ul style={{ margin: "0 0 12px", paddingLeft: 18, color: "var(--warning)" }}>
              {quantities.warnings.map((w, i) => <li key={i} style={{ fontSize: "var(--text-sm)" }}>{w}</li>)}
            </ul>
          )}
          {/* Staff-only notice (not shown to the client): the GIA was estimated from the roof
              footprint because no space carries a floor finish. */}
          {quantities.gia_source === "roof_proxy" && (
            <p style={{ margin: "0 0 12px", fontSize: "var(--text-sm)", color: "var(--warning)" }}>
              ⚠ Floor area estimated from the roof footprint — no FloorCovering set on the model's spaces.
              Assign a floor finish to new/replacement spaces in Vectorworks (leave existing rooms blank)
              for a model-derived GIA driving substructure, floor finishes and M&amp;E.
            </p>
          )}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {SPEC_AXES.map((axis) => (
              <label key={axis.key} style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--fg-muted)" }}>
                {axis.label}
                {specInfo?.detail?.[axis.key]?.confidence === "low" ? " ⚠" : ""}
                <select value={spec[axis.key] ?? axis.values[0]} onChange={(e) => setSpec({ ...spec, [axis.key]: e.target.value })}>
                  {axis.values.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
                </select>
              </label>
            ))}
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--fg-muted)" }}>
              Property type
              <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
                {PROPERTY_TYPES.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
              </select>
            </label>
          </div>
          {/* Kitchen/bathroom confirmation — the parser detects them anywhere in the model, not
              just the new-build zone, so staff confirm whether the extension includes them. */}
          <div style={{ display: "flex", gap: 20, marginTop: 12, fontSize: "var(--text-base)", color: "var(--fg)" }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={includeKitchen} onChange={(e) => setIncludeKitchen(e.target.checked)} />
              New kitchen in extension
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="checkbox" checked={includeBathroom} onChange={(e) => setIncludeBathroom(e.target.checked)} />
              New bathroom in extension
            </label>
          </div>
          {specInfo?.needs_manual_review && (
            <p style={{ color: "var(--warning)", fontSize: "var(--text-sm)", marginTop: 8 }}>Spec inferred at low confidence — please review the selections above.</p>
          )}
          <div style={{ marginTop: 14 }}>
            <Button loading={busy === "generating"} onClick={generate}>Generate Schedule of Works + cost plan</Button>
          </div>
        </section>
      )}

      {/* 3 — Result */}
      {phase === "done" && costPlan && (
        <section>
          <h3 style={{ margin: "0 0 8px" }}>3 · Indicative build cost</h3>
          <div style={{ fontSize: "var(--text-2xl)", fontWeight: 900, color: "var(--fg)" }}>{GBP.format(costPlan.total)}</div>
          <p style={{ color: "var(--fg-muted)", fontSize: "var(--text-sm)", margin: "4px 0 14px" }}>
            Excl. VAT, fees · ±15% · subtotal {GBP.format(costPlan.element_subtotal)} + prelims {GBP.format(costPlan.preliminaries)} + contingency {GBP.format(costPlan.contingency)}
          </p>
          <table style={{ width: "100%", maxWidth: 560, borderCollapse: "collapse", marginBottom: 14 }}>
            <tbody>
              {[...costPlan.elements]
                .sort((a, b) => costElementSortKey(a.element) - costElementSortKey(b.element))
                .map((e) => (
                <tr key={e.element}>
                  <td style={{ padding: "6px 0", borderTop: "1px solid var(--border)" }}>{e.label}</td>
                  <td style={{ padding: "6px 0", borderTop: "1px solid var(--border)", textAlign: "right", color: "var(--fg-muted)" }}>{GBP.format(e.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {costPlan.total_inc_vat != null && (
            <p style={{ fontSize: "var(--text-base)", color: "var(--fg)", margin: "0 0 14px" }}>
              Total (ex VAT) {GBP.format(costPlan.total)} · VAT ({costPlan.vat_rate ?? 20}%) {GBP.format(costPlan.vat ?? 0)} ·{" "}
              <strong>Total incl. VAT {GBP.format(costPlan.total_inc_vat)}</strong>
            </p>
          )}
          {abnormals.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <strong style={{ fontSize: "var(--text-base)" }}>Site risks (not in the figure):</strong>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {abnormals.map((a, i) => <li key={i} style={{ fontSize: "var(--text-sm)" }}>{a.risk} — {a.likelihood} · {a.cost_indicative}</li>)}
              </ul>
            </div>
          )}
          {narrative && <p style={{ fontSize: "var(--text-base)", lineHeight: "var(--leading-relaxed)", maxWidth: 640 }}>{narrative}</p>}
          <div style={{ display: "flex", gap: 12 }}>
            <Button loading={busy === "pdf"} onClick={downloadPdf}>Download PDF</Button>
            <Button variant="secondary" loading={busy === "xlsx"} onClick={downloadExcel}>Download Excel</Button>
          </div>
        </section>
      )}
    </div>
  );
}
